import { useContext, createContext } from 'react';
import type { Dispatch, ReactNode } from 'react';
import { useGetJob } from './job.hooks';
import type { Job } from './job.schema';
import { Heading } from '@checkatrade/web-ui';

const JobContext = createContext<{
  job: Job | null;
  opportunityId?: string;
  tradeId?: string;
  tradeName?: string;
  setJob: Dispatch<Job | null>;
  loading: boolean;
  refetchJob: () => void;
} | null>(null);

export const useJobContext = () => {
  const context = useContext(JobContext);

  if (context === null) {
    throw new Error('useJobContext must be used inside of JobProvider');
  }

  return context;
};

type ChildrenFn = (payload: { job: Job | null }) => ReactNode;

export const JobProvider = ({
  jobId,
  defaultJob = null,
  children,
  tradeId,
  tradeName,
  opportunityId,
}: {
  jobId: string;
  children: ReactNode | ChildrenFn;
  defaultJob?: Job | null;
  tradeId?: string;
  tradeName?: string;
  opportunityId?: string;
}) => {
  const { job, setJob, loading, fetchJob } = useGetJob(jobId, defaultJob);

  return (
    <JobContext.Provider
      value={
        {
          job,
          opportunityId,
          tradeId,
          tradeName,
          setJob,
          loading,
          refetchJob: fetchJob,
        } as const
      }
    >
      {typeof children === 'function' ? (
        children({ job })
      ) : job ? (
        children
      ) : (
        <div className="flex flex-1 items-center justify-center">
          <Heading as="h2" variant="titleLG">
            Job not found
          </Heading>
        </div>
      )}
    </JobContext.Provider>
  );
};
